// Doc editor — replaces the legacy inline JS in document_edit.twig and the
// monolithic ca/docs/docs.js editor sections. Activates when `.js-doc-editor`
// is in the DOM.
//
// Pulls config from the wrapper element's data attributes:
//   data-doc-id, data-template-id, data-practice-id, data-admin-sec-id,
//   data-is-practice
//
// Behaviors:
//   - One area for editing and reading: the rich editor (bsee-editor.ts) shows the Markdown
//     formatted as you type; the textarea behind it holds the Markdown that gets saved
//   - Autosave after a short idle (POST to /ca/docs/api/autosaveDocument.php)
//   - Save button + revision dropdown
//   - First-save flow: stamps doc id back into the URL, links the practice tag
//   - Template seeding: if `#js-doc-template-tags` JSON exists, those tags are
//     attached on first save

import { addTag } from './doc-tags';
import { setEditorMarkdown } from './bsee-editor';

interface SetDocResponse {
    'doc id': string;
    data: { title: string };
}

interface RevisionResponse {
    title: { title: string; public: string };
    data: { body: string };
    body: string;
}

interface GetDocResponse {
    title: { title: string; public: string };
    reg: string;
    parse: string;
    idd: string;
}

interface TemplateResponse {
    template: {
        id: string;
        title: string;
        body: string;
        parseBody: string;
    };
}

async function postForm<T>(url: string, data: Record<string, string>): Promise<T> {
    const body = new URLSearchParams(data);
    // X-Requested-With marks this as AJAX so the server skips the Tracy debug bar —
    // otherwise its HTML is appended to the JSON and JSON.parse() below fails.
    const res = await fetch(url, {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    const text = await res.text();
    try {
        return JSON.parse(text) as T;
    } catch {
        return text as unknown as T;
    }
}

function showSaveStatus(text: string, fadeMs = 500): void {
    let msg = document.getElementById('js-out-msg');
    if (!msg) {
        msg = document.createElement('span');
        msg.id = 'js-out-msg';
        document.querySelector('.left-section')?.appendChild(msg);
    }
    msg.style.opacity = '1';
    msg.textContent = text;
    setTimeout(() => {
        if (msg) {
            msg.style.transition = `opacity ${fadeMs}ms`;
            msg.style.opacity = '0';
        }
    }, 100);
}

function readTemplateTags(): string[] {
    const node = document.getElementById('js-doc-template-tags');
    if (!node?.textContent) {
        return [];
    }
    try {
        const parsed = JSON.parse(node.textContent);
        return Array.isArray(parsed) ? parsed.map((t) => String(t)) : [];
    } catch {
        return [];
    }
}

function initDocEditor(): void {
    const root = document.querySelector<HTMLElement>('.js-doc-editor');
    if (!root) {
        return;
    }

    const titleEl     = document.getElementById('js-doc-title') as HTMLInputElement;
    const bodyEl      = document.getElementById('js-doc') as HTMLTextAreaElement;
    const hiddenEl    = document.getElementById('js-hidden-id') as HTMLInputElement;
    const publicEl    = document.getElementById('js-doc-public') as HTMLInputElement;
    const saveBtn     = document.getElementById('js-save-but');
    const newBtn      = document.getElementById('js-new-document');
    const pubViewLink = document.getElementById('js-doc-public-view') as HTMLAnchorElement | null;
    const viewLink    = document.getElementById('js-doc-view') as HTMLAnchorElement | null;
    const fullViewLnk = document.getElementById('js-doc-full-view') as HTMLAnchorElement | null;
    const revSelect   = document.getElementById('js-doc-revisions') as HTMLSelectElement | null;

    if (!titleEl || !bodyEl || !hiddenEl) {
        return;
    }

    const docId        = root.dataset.docId || '';
    const templateId   = root.dataset.templateId || '';
    const practiceId   = root.dataset.practiceId || '';
    const adminSecId   = root.dataset.adminSecId || '';
    const isPractice   = root.dataset.isPractice === '1';
    const templateTags = readTemplateTags();

    const hasDoc      = !!docId;
    const hasTemplate = !!templateId && !hasDoc;

    // #js-doc-public is a BS5 switch checkbox: checked = public ('1'), unchecked = dashboard ('0').
    function getPublic(): string {
        return publicEl.checked ? '1' : '0';
    }
    function setPublic(value: string | number): void {
        publicEl.checked = String(value) === '1';
        updatePublicView();
    }

    // The public-view button links to the no-auth /ca/docs/pub/ view and is shown only
    // while the doc is public AND already saved (has a real id).
    function currentDocId(): string {
        return hiddenEl.getAttribute('docid') || hiddenEl.dataset.docid || '';
    }
    function updatePublicView(): void {
        if (!pubViewLink) {
            return;
        }
        const id = currentDocId();
        const show = publicEl.checked && parseInt(id, 10) > 0;
        if (show) {
            pubViewLink.href = `/ca/docs/pub/${id}`;
            pubViewLink.classList.remove('d-none');
        } else {
            pubViewLink.classList.add('d-none');
        }
    }

    // Persist a public/private change immediately (existing docs only). A brand-new,
    // unsaved doc has no id yet — there's nothing to update, so we just refresh the UI
    // and let the first manual Save carry the chosen visibility.
    async function persistPublic(): Promise<void> {
        const currentId = currentDocId();
        if (parseInt(currentId, 10) > 0) {
            await postForm<SetDocResponse>('/ca/docs/api/setDocument.php', {
                title: titleEl.value,
                admin: adminSecId,
                pracVal: practiceId,
                docPublic: getPublic(),
                body: bodyEl.value,
                updateId: currentId,
            });
            showSaveStatus('Saved', 3000);
        }
    }

    // React live when the user flips the Public switch: update the public-view button
    // and persist the new visibility right away.
    publicEl.addEventListener('change', () => {
        updatePublicView();
        persistPublic();
    });

    // ── Idle-autosave ──────────────────────────────────────────────
    // The rich editor (bsee-editor.ts) shows the Markdown formatted as it is typed, so there is
    // no separate preview. It fires `input` on the textarea after each edit; the editor's own
    // content loads (setBody) deliberately don't, so loading a doc never autosaves it.
    let typingTimer: number | null = null;
    let dirty = false;

    // A loaded doc's existing mentions were handled long ago: only new ones get asked about.
    function setBody(markdown: string, serverHtml?: string): void {
        setEditorMarkdown(bodyEl, markdown, serverHtml);
        rememberMentions(markdown);
    }

    bodyEl.addEventListener('input', () => {
        dirty = true;
        if (typingTimer) {
            window.clearTimeout(typingTimer);
        }
        typingTimer = window.setTimeout(async () => {
            if (!dirty) {
                return;
            }
            try {
                await autoSave();
                dirty = false;
            } catch {
                showSaveStatus('Save failed');
                // dirty stays true so the next tick retries.
            }
        }, 1000);
    });

    // ── Save flows ─────────────────────────────────────────────────
    // The first autosave after a doc opens asks the server to keep the doc as it was (the editor
    // rewrites Markdown on save; this keeps an older revision to go back to).
    let keptOriginal = false;

    async function autoSave(): Promise<void> {
        const currentId = hiddenEl.getAttribute('docid') || hiddenEl.dataset.docid || '';
        if (currentId) {
            const payload: Record<string, string> = {
                id: currentId,
                author: adminSecId,
                body: bodyEl.value,
            };
            if (!keptOriginal && hasDoc) {
                payload.keepOriginal = '1';
            }
            const res = await postForm<SetDocResponse>('/ca/docs/api/autosaveDocument.php', payload);
            keptOriginal = true;
            showSaveStatus('Saved');
            if (res['doc id']) {
                hiddenEl.setAttribute('docid', res['doc id']);
                updatePublicView();
            }
        } else if (bodyEl.value.length > 0) {
            await firstSave();
        }
    }

    async function firstSave(): Promise<void> {
        const res = await postForm<SetDocResponse>('/ca/docs/api/setDocument.php', {
            title: titleEl.value,
            admin: adminSecId,
            pracVal: practiceId,
            docPublic: getPublic(),
            body: bodyEl.value,
            updateId: '',
        });
        onFirstSave(res);
    }

    function onFirstSave(res: SetDocResponse): void {
        showSaveStatus('Saved', 3000);
        titleEl.value = res.data.title;
        hiddenEl.setAttribute('docid', res['doc id']);
        updatePublicView();
        if (fullViewLnk) {
            fullViewLnk.classList.remove('disabled');
            fullViewLnk.href = `/ca/docs/${res['doc id']}`;
        }
        if (viewLink) {
            viewLink.classList.remove('disabled');
            viewLink.href = `/ca/customer.php?page=document&practice_id=${practiceId}&doc=${res['doc id']}`;
        }

        // Template-seeded tags
        templateTags.forEach((tag) => addTag(tag, res['doc id']));

        // Practice ownership is recorded server-side via doc_customer (setDocument.php
        // passes pracVal → createDocument → attachToPractice). The practice id is no
        // longer mirrored as a doc_tag, so we don't add it here — just sync the URL.
        if (isPractice && practiceId) {
            history.replaceState(
                null,
                '',
                `customer.php?page=document_edit&practice_id=${practiceId}&doc=${res['doc id']}`,
            );
        }
    }

    // ── Mentions: ask before emailing ──────────────────────────────
    // Docs autosave, so there is no single "save" moment to email on. After an explicit Save we
    // ask once about each person newly @mentioned since the doc was loaded (or last asked about).
    interface MentionPerson {
        token: string;
        name: string;
        initials: string;
    }
    const people: MentionPerson[] = JSON.parse(bodyEl.dataset.bseeMentions || '[]');
    const alreadyAsked = new Set<string>();

    function mentionedPeople(markdown: string): MentionPerson[] {
        const found: MentionPerson[] = [];
        for (const match of markdown.matchAll(/(?<![\w@])@@?([A-Za-z0-9]+)/g)) {
            const typed = match[1].toLowerCase();
            const person = people.find((p) => p.token.toLowerCase() === typed || p.initials.toLowerCase() === typed);
            if (person && !found.includes(person)) {
                found.push(person);
            }
        }
        return found;
    }

    function rememberMentions(markdown: string): void {
        mentionedPeople(markdown).forEach((person) => alreadyAsked.add(person.token));
    }

    async function askToNotify(docIdToNotify: string): Promise<void> {
        const fresh = mentionedPeople(bodyEl.value).filter((person) => !alreadyAsked.has(person.token));
        if (fresh.length === 0 || parseInt(docIdToNotify, 10) <= 0) {
            return;
        }
        fresh.forEach((person) => alreadyAsked.add(person.token));
        const names = fresh.map((person) => person.name).join(', ');
        if (!window.confirm(`Notify ${names} about this document?`)) {
            return;
        }
        await postForm('/ca/docs/api/notifyMentions.php', {
            id: docIdToNotify,
            names: fresh.map((person) => person.token).join(','),
        });
        showSaveStatus('Notified', 3000);
    }

    async function manualSave(): Promise<void> {
        const currentId = hiddenEl.getAttribute('docid') || hiddenEl.dataset.docid || '';
        if (parseInt(currentId, 10) > 0) {
            await postForm<SetDocResponse>('/ca/docs/api/setDocument.php', {
                title: titleEl.value,
                admin: adminSecId,
                pracVal: practiceId,
                docPublic: getPublic(),
                body: bodyEl.value,
                updateId: currentId,
            });
            showSaveStatus('Saved', 3000);
        } else {
            await firstSave();
        }
        await askToNotify(hiddenEl.getAttribute('docid') || hiddenEl.dataset.docid || '');
    }

    saveBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        manualSave();
    });

    newBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        if (isPractice) {
            location.replace(`/ca/customer.php?page=document_edit&practice_id=${practiceId}`);
        } else {
            location.replace('/ca/docs/new');
        }
    });

    revSelect?.addEventListener('change', async (e) => {
        const target = e.currentTarget as HTMLSelectElement;
        const res = await postForm<RevisionResponse>('/ca/docs/api/getRevision.php', { id: target.value });
        titleEl.value = res.title.title;
        setBody(res.data.body, res.body);
        setPublic(res.title.public);
    });

    // ── Initial load ───────────────────────────────────────────────
    (async () => {
        if (hasDoc) {
            const res = await postForm<GetDocResponse>('/ca/docs/api/getDocument.php', { id: docId });
            titleEl.value = res.title.title;
            setBody(res.reg, res.parse);
            hiddenEl.setAttribute('docid', res.idd);
            setPublic(res.title.public);
        } else if (hasTemplate) {
            const res = await postForm<TemplateResponse>('/ca/docs/api/getTemplate.php', { tempid: templateId });
            const t = res.template;
            titleEl.value = t.title;
            if (t.id !== '0') {
                setBody(t.body, t.parseBody);
                hiddenEl.setAttribute('tempid', t.id);
                fullViewLnk?.classList.add('disabled');
                viewLink?.classList.add('disabled');
                setPublic('1');
            }
        } else {
            titleEl.value = '';
            setBody('');
            hiddenEl.setAttribute('docid', '');
            fullViewLnk?.classList.add('disabled');
            viewLink?.classList.add('disabled');
            setPublic('1');
        }
    })();
}

document.addEventListener('DOMContentLoaded', initDocEditor);
